/**
 * Chart Renderer Component
 * 
 * Renders various chart types from JSON data in markdown code blocks
 * Supports: Chart.js charts, Gantt charts, Recharts
 */

import React, { useEffect, useRef } from 'react';
import { Box, Paper, Typography } from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions
} from 'chart.js';
import { Line, Bar, Pie, Doughnut } from 'react-chartjs-2';
import { LineChart, Line as RLine, BarChart, Bar as RBar, PieChart, Pie as RPie, Cell, XAxis, YAxis, CartesianGrid, Tooltip as RTooltip, Legend as RLegend, ResponsiveContainer } from 'recharts';
import Gantt from 'frappe-gantt';
// Note: frappe-gantt CSS is not properly exported, using custom styles
import '../../styles/gantt.css';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  BarElement,
  ArcElement,
  Title,
  Tooltip,
  Legend
);

interface ChartRendererProps {
  type: string;
  data: any;
  options?: any;
}

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d', '#ffc658', '#ff7c7c'];

export const ChartRenderer: React.FC<ChartRendererProps> = ({ type, data, options = {} }) => {
  const ganttRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (type === 'gantt' && ganttRef.current && data.tasks) {
      // Clear previous chart and any existing classes
      ganttRef.current.innerHTML = '';
      ganttRef.current.className = '';
      
      try {
        console.log('📊 Rendering Gantt chart with tasks:', data.tasks);
        
        // Create Gantt instance
        const ganttInstance = new Gantt(ganttRef.current, data.tasks, {
          view_mode: data.view_mode || 'Week',
          bar_height: 35,
          bar_corner_radius: 3,
          arrow_curve: 5,
          padding: 18,
          date_format: 'YYYY-MM-DD',
          language: 'en',
          custom_popup_html: (task: any) => {
            return `
              <div style="padding: 10px; font-size: 12px;">
                <h5 style="margin: 0 0 8px 0;">${task.name}</h5>
                <p style="margin: 0;"><strong>Start:</strong> ${task._start.format('YYYY-MM-DD')}</p>
                <p style="margin: 0;"><strong>End:</strong> ${task._end.format('YYYY-MM-DD')}</p>
                <p style="margin: 0;"><strong>Progress:</strong> ${task.progress}%</p>
              </div>
            `;
          },
          on_click: (task: any) => {
            console.log('Clicked task:', task);
          },
          ...options
        });
        console.log('✅ Gantt chart rendered:', ganttInstance);
        
        // Force style the SVG after creation
        setTimeout(() => {
          if (ganttRef.current) {
            const svg = ganttRef.current.querySelector('svg');
            if (svg) {
              svg.style.backgroundColor = 'white';
              
              // Force colors on all elements since CSS isn't being applied
              const allText = svg.querySelectorAll('text');
              const allRects = svg.querySelectorAll('rect');
              const allPaths = svg.querySelectorAll('path');
              
              // Apply colors to text elements
              allText.forEach((text: any) => {
                if (!text.getAttribute('fill')) {
                  text.setAttribute('fill', '#333333');
                }
              });
              
              // Apply colors to bars - FORCE them even if they have a fill
              const bars = svg.querySelectorAll('.bar');
              console.log('Found', bars.length, 'bars');
              if (bars.length > 0) {
                const firstBar = bars[0] as SVGElement;
                console.log('First bar before styling:', {
                  fill: firstBar.getAttribute('fill'),
                  stroke: firstBar.getAttribute('stroke'),
                  class: firstBar.getAttribute('class')
                });
              }
              bars.forEach((bar: any) => {
                const svgBar = bar as SVGElement;
                svgBar.setAttribute('fill', '#42a5f5');
                svgBar.setAttribute('stroke', '#1976d2');
                svgBar.setAttribute('stroke-width', '0');
                svgBar.style.fill = '#42a5f5';
              });
              if (bars.length > 0) {
                const firstBar = bars[0] as SVGElement;
                console.log('First bar after styling:', {
                  fill: firstBar.getAttribute('fill'),
                  stroke: firstBar.getAttribute('stroke'),
                  styleFill: firstBar.style.fill
                });
              }
              
              const barProgress = svg.querySelectorAll('.bar-progress');
              barProgress.forEach((bar: any) => {
                bar.setAttribute('fill', '#1976d2');
              });
              
              const barLabels = svg.querySelectorAll('.bar-label');
              barLabels.forEach((label: any) => {
                label.setAttribute('fill', '#ffffff');
              });
              
              // Grid rows
              const gridRows = svg.querySelectorAll('.grid-row');
              gridRows.forEach((row: any, index: number) => {
                if (!row.getAttribute('fill')) {
                  row.setAttribute('fill', index % 2 === 0 ? '#ffffff' : '#fafafa');
                }
              });
              
              // Grid header
              const gridHeader = svg.querySelectorAll('.grid-header');
              gridHeader.forEach((header: any) => {
                if (!header.getAttribute('fill')) {
                  header.setAttribute('fill', '#f5f5f5');
                }
              });
              
              // Lines and paths
              allPaths.forEach((path: any) => {
                if (!path.getAttribute('stroke')) {
                  path.setAttribute('stroke', '#666666');
                }
              });
              
              console.log('✅ Applied colors to Gantt chart elements');
              
              // Debug: Log actual SVG HTML to see what's rendered
              console.log('SVG outer HTML (first 500 chars):', svg.outerHTML.substring(0, 500));
              console.log('SVG computed style background:', window.getComputedStyle(svg).backgroundColor);
              console.log('Container computed background:', window.getComputedStyle(ganttRef.current).backgroundColor);
            }
          }
        }, 150);
      } catch (err) {
        console.error('❌ Error rendering Gantt chart:', err);
      }
    }
  }, [type, data, options]);

  // Chart.js charts
  if (type === 'bar' || type === 'line' || type === 'pie' || type === 'doughnut') {
    const chartOptions: ChartOptions<any> = {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: {
          position: 'top' as const,
        },
        title: {
          display: !!data.title,
          text: data.title || '',
        },
      },
      ...options
    };

    return (
      <Paper sx={{ p: 2, my: 2, bgcolor: 'grey.50' }}>
        <Box sx={{ height: 300 }}>
          {type === 'bar' && <Bar data={data} options={chartOptions} />}
          {type === 'line' && <Line data={data} options={chartOptions} />}
          {type === 'pie' && <Pie data={data} options={chartOptions} />}
          {type === 'doughnut' && <Doughnut data={data} options={chartOptions} />}
        </Box>
      </Paper>
    );
  }

  // Recharts (alternative syntax)
  if (type === 'recharts:line' || type === 'recharts:bar' || type === 'recharts:pie') {
    return (
      <Paper sx={{ p: 2, my: 2, bgcolor: 'grey.50' }}>
        <Box sx={{ height: 300 }}>
          <ResponsiveContainer width="100%" height="100%">
            {type === 'recharts:line' && (
              <LineChart data={data.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={data.xKey || 'name'} />
                <YAxis />
                <RTooltip />
                <RLegend />
                {data.lines?.map((line: any, index: number) => (
                  <RLine 
                    key={index} 
                    type="monotone" 
                    dataKey={line.dataKey} 
                    stroke={line.color || COLORS[index % COLORS.length]} 
                    name={line.name}
                  />
                ))}
              </LineChart>
            )}
            {type === 'recharts:bar' && (
              <BarChart data={data.data}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey={data.xKey || 'name'} />
                <YAxis />
                <RTooltip />
                <RLegend />
                {data.bars?.map((bar: any, index: number) => (
                  <RBar 
                    key={index} 
                    dataKey={bar.dataKey} 
                    fill={bar.color || COLORS[index % COLORS.length]} 
                    name={bar.name}
                  />
                ))}
              </BarChart>
            )}
            {type === 'recharts:pie' && (
              <PieChart>
                <RPie
                  data={data.data}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={(entry) => entry.name}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey={data.dataKey || 'value'}
                >
                  {data.data.map((entry: any, index: number) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </RPie>
                <RTooltip />
                <RLegend />
              </PieChart>
            )}
          </ResponsiveContainer>
        </Box>
      </Paper>
    );
  }

  // Gantt chart - Clean list view with horizontal bars
  if (type === 'gantt') {
    return (
      <Paper sx={{ p: 3, my: 2, bgcolor: 'white', overflow: 'visible' }}>
        {data.title && (
          <Typography variant="h6" gutterBottom sx={{ mb: 3 }}>
            {data.title}
          </Typography>
        )}
        
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {data.tasks.map((task: any, index: number) => {
            const start = new Date(task.start);
            const end = new Date(task.end);
            const duration = Math.ceil((end.getTime() - start.getTime()) / (1000 * 60 * 60 * 24)) + 1;
            
            return (
              <Box key={index}>
                {/* Task Header */}
                <Box sx={{ display: 'flex', alignItems: 'baseline', mb: 0.5, gap: 2 }}>
                  <Typography 
                    variant="body2" 
                    sx={{ 
                      fontWeight: 600,
                      color: 'text.primary',
                      flex: '0 0 auto',
                      maxWidth: '60%'
                    }}
                  >
                    {task.name}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {start.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - {end.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                    ({duration} days)
                  </Typography>
                </Box>
                
                {/* Progress Bar */}
                <Box 
                  sx={{ 
                    width: '100%',
                    height: 32,
                    bgcolor: 'grey.100',
                    borderRadius: 1,
                    overflow: 'hidden',
                    position: 'relative',
                    border: '1px solid',
                    borderColor: 'grey.300'
                  }}
                >
                  <Box
                    sx={{
                      width: '100%',
                      height: '100%',
                      bgcolor: task.progress === 100 ? 'success.light' : 'primary.light',
                      opacity: 0.3,
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      width: `${task.progress}%`,
                      height: '100%',
                      bgcolor: task.progress === 100 ? 'success.main' : 'primary.main',
                      display: 'flex',
                      alignItems: 'center',
                      px: 1.5,
                      transition: 'width 0.3s ease'
                    }}
                  >
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'white', 
                        fontWeight: 700,
                        fontSize: '0.75rem'
                      }}
                    >
                      {task.progress}%
                    </Typography>
                  </Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </Paper>
    );
  }

  // Fallback
  return (
    <Paper sx={{ p: 2, my: 2, bgcolor: 'error.light' }}>
      <Typography variant="body2" color="error">
        Unsupported chart type: {type}
      </Typography>
    </Paper>
  );
};

